import { useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/client';

import { GET_USER_INCOME } from '../../graphql/queries/financeQueries';
import { getCloudFrontImageUrl } from '../../util/cloudFrontUtils';
import awsConfig from '../../config/aws-config';

const processStudioImage = (studio) => {
  if (!studio) return { processedStudioImage: '', hasImages: false };

  let imageKey = null;

  if (Array.isArray(studio.images) && studio.images.length > 0) {
    imageKey = studio.images[0];
  } else if (studio.image) {
    imageKey = studio.image;
  }

  if (!imageKey) {
    return { processedStudioImage: '', hasImages: false };
  }

  try {
    const url = getCloudFrontImageUrl(imageKey, {
      width: 600,
      height: 450,
      fit: 'cover',
    });

    return {
      processedStudioImage: url,
      hasImages: true,
    };
  } catch (error) {
    try {
      const cleanKey = imageKey.startsWith('public/')
        ? imageKey
        : `public/${imageKey}`;

      const fallbackUrl = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;

      return {
        processedStudioImage: fallbackUrl,
        hasImages: true,
      };
    } catch (fallbackError) {
      return { processedStudioImage: '', hasImages: false };
    }
  }
};

export const useGetUserIncome = (studioOwnerID, { limit = 50 } = {}) => {
  const {
    data,
    loading,
    error,
    refetch,
  } = useQuery(GET_USER_INCOME, {
    variables: { studioOwnerID, limit },
    skip: !studioOwnerID,
    fetchPolicy: 'cache-and-network',
  });

  const income = useMemo(() => {
    const items = data?.bookingsByStudioOwnerID?.items || [];

    return items
      .filter(
        (b) =>
          b.paymentStatus === 'PAID' ||
          b.status === 'COMPLETED' ||
          b.status === 'CONFIRMED'
      )
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      .map((booking) => {
        const studio = booking?.Studio || null;
        const { processedStudioImage, hasImages } = processStudioImage(studio);

        return {
          ...booking,
          Studio: {
            ...studio,
            processedStudioImage,
            hasImages,
          },
        };
      });
  }, [data]);

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    income,
    loading,
    error,
    refresh,
  };
};


